/**
 * Cart shipping estimator
 * Calls Shopify's public /cart/shipping_rates.json endpoint, which returns the
 * store's actual configured shipping rates for a destination. No checkout needed,
 * works on all Shopify plans. Inputs: ZIP + country (+ optional province for accuracy).
 */
class CartShippingEstimator extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-cse-form]');
    this.zipInput = this.querySelector('[data-cse-zip]');
    this.countryInput = this.querySelector('[data-cse-country]');
    this.button = this.querySelector('[data-cse-submit]');
    this.results = this.querySelector('[data-cse-results]');
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
  }

  connectedCallback() {
    if (!this.form) return;
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  onSubmit(event) {
    event.preventDefault();
    const zip = (this.zipInput.value || '').trim();
    const country = (this.countryInput.value || '').trim();

    if (!country) {
      this.renderMessage('Please choose a country.', 'error');
      return;
    }

    this.setLoading(true);

    const params = new URLSearchParams();
    if (zip) params.set('shipping_address[zip]', zip);
    params.set('shipping_address[country]', country);

    // Shopify requires a province/state for US & Canada. Derive it from the
    // postal code so the customer only has to type a ZIP.
    const province = this.resolveProvince(zip, country);
    if (province) params.set('shipping_address[province]', province);

    fetch(`${window.Shopify?.routes?.root || '/'}cart/shipping_rates.json?${params.toString()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, status: res.status, data })))
      .then(({ ok, data }) => {
        if (!ok || !data || data.shipping_rates == null) {
          const msg = this.extractError(data) || 'We couldn’t estimate shipping for that destination. Please check the ZIP and country.';
          this.renderMessage(msg, 'error');
          return;
        }
        this.renderRates(data.shipping_rates);
      })
      .catch(() => {
        this.renderMessage('Something went wrong fetching rates. Please try again.', 'error');
      })
      .finally(() => this.setLoading(false));
  }

  extractError(data) {
    if (!data) return null;
    // Shopify returns { "shipping_address": { "zip": ["is not valid"] } } style errors
    const firstKey = Object.keys(data)[0];
    const val = firstKey ? data[firstKey] : null;
    if (Array.isArray(val)) return `${firstKey.replace(/_/g, ' ')} ${val[0]}.`;
    if (typeof val === 'string') return val;
    return null;
  }

  renderRates(rates) {
    if (!rates.length) {
      this.renderMessage('No shipping rates are available for this destination. Some items may require a freight quote — use “Convert to Formal Quote” above.', 'info');
      return;
    }

    const items = rates
      .map((rate) => {
        const price = this.formatPrice(rate.price);
        const name = rate.presentment_name || rate.name || 'Shipping';
        return `<li class="cse-rate"><span class="cse-rate__name">${this.escape(name)}</span><span class="cse-rate__price" style="margin-left:0.6rem">${price}</span></li>`;
      })
      .join('');

    this.results.innerHTML = `
      <p class="cse-results__heading">Estimated shipping options:</p>
      <ul class="cse-rates list-unstyled">${items}</ul>
      <p class="cse-results__note">Final rates are confirmed at checkout.</p>
    `;
    this.results.hidden = false;
  }

  renderMessage(text, type) {
    this.results.innerHTML = `<p class="cse-message cse-message--${type}">${this.escape(text)}</p>`;
    this.results.hidden = false;
  }

  /**
   * /cart/shipping_rates.json returns price as a major-unit string ("12.50").
   * Format it using the store's money format.
   */
  formatPrice(price) {
    const amount = parseFloat(price);
    if (isNaN(amount)) return this.escape(String(price));
    const cents = Math.round(amount * 100);
    return this.formatMoney(cents);
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100).toString());
  }

  setLoading(isLoading) {
    this.button.toggleAttribute('disabled', isLoading);
    this.button.classList.toggle('loading', isLoading);
    if (isLoading) {
      this.results.hidden = false;
      this.results.innerHTML = '<p class="cse-message cse-message--info">Estimating…</p>';
    }
  }

  escape(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Returns the 2-letter state/province code for a US ZIP or Canadian postal
   * code, or null if it can't be determined (Shopify then errors gracefully).
   */
  resolveProvince(postal, country) {
    if (!postal) return null;
    const c = country.toLowerCase();
    if (c === 'united states' || c === 'us' || c === 'usa') {
      return this.usStateFromZip(postal);
    }
    if (c === 'canada' || c === 'ca') {
      return this.caProvinceFromPostal(postal);
    }
    return null;
  }

  usStateFromZip(zip) {
    const prefix = parseInt(String(zip).replace(/\D/g, '').slice(0, 3), 10);
    if (isNaN(prefix)) return null;
    // [minPrefix, maxPrefix, stateCode] for the first 3 ZIP digits.
    const ranges = [
      [6, 9, 'PR'], [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'],
      [50, 54, 'VT'], [55, 55, 'MA'], [56, 59, 'VT'], [60, 69, 'CT'], [70, 89, 'NJ'],
      [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'], [200, 205, 'DC'],
      [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'], [270, 289, 'NC'],
      [290, 299, 'SC'], [300, 319, 'GA'], [320, 349, 'FL'], [350, 369, 'AL'],
      [370, 385, 'TN'], [386, 397, 'MS'], [398, 399, 'GA'], [400, 427, 'KY'],
      [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'], [500, 528, 'IA'],
      [530, 549, 'WI'], [550, 567, 'MN'], [570, 577, 'SD'], [580, 588, 'ND'],
      [590, 599, 'MT'], [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'],
      [680, 693, 'NE'], [700, 714, 'LA'], [716, 729, 'AR'], [730, 749, 'OK'],
      [750, 799, 'TX'], [800, 816, 'CO'], [820, 831, 'WY'], [832, 838, 'ID'],
      [840, 847, 'UT'], [850, 865, 'AZ'], [870, 884, 'NM'], [889, 898, 'NV'],
      [900, 961, 'CA'], [967, 968, 'HI'], [970, 979, 'OR'], [980, 994, 'WA'],
      [995, 999, 'AK'],
    ];
    for (const [min, max, state] of ranges) {
      if (prefix >= min && prefix <= max) return state;
    }
    return null;
  }

  caProvinceFromPostal(postal) {
    const letter = String(postal).trim().charAt(0).toUpperCase();
    const map = {
      A: 'NL', B: 'NS', C: 'PE', E: 'NB', G: 'QC', H: 'QC', J: 'QC',
      K: 'ON', L: 'ON', M: 'ON', N: 'ON', P: 'ON', R: 'MB', S: 'SK',
      T: 'AB', V: 'BC', X: 'NT', Y: 'YT',
    };
    return map[letter] || null;
  }
}

if (!customElements.get('cart-shipping-estimator')) {
  customElements.define('cart-shipping-estimator', CartShippingEstimator);
}
